import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { r as remCalc } from './index5.js';
import { D as DomService } from './index3.js';
import { A as AccordionTabsType, a as AccordionTabsAppearance } from './roche-accordion-tabs.types.js';
import { V as VisibilityManager } from './index2.js';
import { d as defineCustomElement$2 } from './roche-anchor2.js';
import { d as defineCustomElement$1 } from './roche-icon2.js';

const rocheAccordionTabsItemCss = ":host{display:block;margin-top:3rem;margin-top:0;display:block}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}:host roche-anchor{transform:translateY(-5rem)}button{all:unset}:host([type=accordion]){padding-bottom:0;transition:padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1);border-bottom:1px solid #c2bab5}:host([type=accordion]) ::slotted(*){margin-top:0 !important}:host([type=accordion]) .title{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:400;font-size:1.125rem;line-height:1.6875rem;display:none;width:100%;position:relative;color:#544f4f;cursor:pointer;justify-content:flex-start;align-items:center;padding:0.75rem 0}:host([type=accordion]) .title strong{font-weight:600}@media (min-width: 48rem){:host([type=accordion]) .title{font-size:1.125rem;line-height:1.8125rem}}@media (min-width: 90rem){:host([type=accordion]) .title{font-size:1.25rem;line-height:1.875rem}}@media (min-width: 48rem){:host([type=accordion]) .title{padding:1rem 0}}:host([type=accordion]) .title:focus,:host([type=accordion]) .title:focus-visible{outline:var(--color-interactive--focus) dashed 2px;outline-offset:unset}:host([type=accordion]) .title:focus:not(:focus-visible){outline:none}:host([type=accordion]) .title--visible{display:flex}:host([type=accordion]) .title roche-icon{fill:currentcolor}:host([type=accordion]) .title roche-icon:first-child{margin-right:1rem}:host([type=accordion]) .title roche-icon:last-child{margin-left:auto}:host([type=accordion]) .content{overflow:hidden;opacity:0;transition-property:opacity, max-height;transition-duration:0.4s;transition-timing-function:cubic-bezier(0.4, 0, 0.2, 1)}:host([type=accordion]) .content--visible{opacity:1;height:auto}:host([appearance=pipeline]){border:none !important;margin-bottom:0 !important}:host([appearance=pipeline]) .title{font-size:1.125rem}:host([is-overflow-y-scrollable=true]) ::slotted(roche-select){height:12.5rem;overflow:scroll;overflow-x:hidden;overscroll-behavior:contain}:host(:first-of-type[type=accordion]){border-top:1px solid #c2bab5}:host([type=accordion][open]){padding-bottom:1rem;}@media (min-width: 48rem){:host([type=accordion][open]){padding-bottom:1.5rem}}:host([type=accordion][open]) .title{color:var(--color-interactive--primary-hover)}:host([type=accordion]:not([open]):hover){border-bottom:2px solid var(--color-interactive--primary-hover);margin-bottom:-1px}:host([type=accordion]:not([open]):hover) [appearance=pipeline]{margin-bottom:0 !important}:host([type=accordion]:not([open]):hover) .title{color:var(--color-interactive--primary-hover)}:host([type=tabs]){}:host([type=tabs]) .title{display:none}:host([type=tabs]) ::slotted(roche-slider){margin-top:3rem !important}@media (min-width: 90rem){:host([type=tabs]) ::slotted(roche-slider){margin-top:4.5rem !important}}:host([type=tabs]) .content{display:none}:host([type=tabs]) .content--visible{display:block}:host([dir=rtl]) .title div{flex-grow:1}:host([dir=rtl]) .title roche-icon:first-child{margin-left:1rem;margin-right:0}";
const RocheAccordionTabsItemStyle0 = rocheAccordionTabsItemCss;

const RocheAccordionTabsItem = /*@__PURE__*/ proxyCustomElement(class RocheAccordionTabsItem extends HTMLElement {
    handleItemOpenEvent(value) {
        if (value) {
            Array.from(this.element.children).forEach((element) => {
                element.dispatchEvent(new CustomEvent('rocheAccordionTabsItemOpen'));
            });
            const scrollTarget = this.element;
            if (this.type !== AccordionTabsType.ACCORDION || !scrollTarget) {
                return;
            }
            const onTransitionEnd = (event) => {
                if (event.target === scrollTarget) {
                    VisibilityManager.scrollTo(scrollTarget);
                    setTimeout(() => {
                        const header = document.querySelector('roche-header');
                        if (!header.classList.contains('hidden')) {
                            window.scrollBy(0, 1);
                        }
                    }, 750);
                    scrollTarget.removeEventListener('transitionend', onTransitionEnd);
                }
            };
            scrollTarget.addEventListener('transitionend', onTransitionEnd);
        }
    }
    get contentElement() {
        var _a;
        return (_a = this.contentElementParent) === null || _a === void 0 ? void 0 : _a.querySelector('*');
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.toggleEvent = createEvent(this, "rocheAccordionTabsItemToggle", 7);
        this.headline = undefined;
        this.icon = undefined;
        this.type = undefined;
        this.isOverflowYScrollable = false;
        this.appearance = AccordionTabsAppearance.DEFAULT;
        this.open = false;
        this.enableJumpmark = true;
        this.updateOnContentChange = true;
        this.handleClick = this.handleClick.bind(this);
        this.updateMaxHeight = debounce_1.debounce(this.updateMaxHeight.bind(this), 50);
    }
    async getTabIndex() {
        return Array
            .prototype
            .indexOf
            .call(this.element.parentElement.children, this.element) - 1;
    }
    /** Expands component and returns a promise that resolves once the the transition is finished */
    async ensureVisibility() {
        await customElements.whenDefined('roche-accordion-tabs-item');
        const parent = this.element.parentElement;
        await parent.syntheticHandleToggle(await this.getTabIndex());
        // TODO: get transition duration from css
        const transitionDuration = 800;
        await new Promise((resolve) => setTimeout(resolve, transitionDuration));
    }
    componentWillLoad() {
        DomService.createObserver(this.element, this.updateMaxHeight);
    }
    componentDidLoad() {
        this.manageMutationObserver();
        DomService.handleTextDirection(this.element);
    }
    disconnectedCallback() {
        var _a;
        (_a = this.mutationObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    }
    componentDidUpdate() {
        this.updateMaxHeight();
    }
    get icons() {
        if (this.open) {
            return this.appearance === AccordionTabsAppearance.PIPELINE ? 'chevron-up' : 'minus';
        }
        return this.appearance === AccordionTabsAppearance.PIPELINE ? 'chevron-down' : 'plus';
    }
    render() {
        if (this.type === undefined) {
            return h("div", null);
        }
        return (h(Host, { appearance: true }, h("button", { class: { title: true, 'title--visible': this.type === AccordionTabsType.ACCORDION }, onClick: this.handleClick }, this.icon && h("roche-icon", { icon: this.icon, height: `${remCalc(24)}rem` }), h("div", null, this.headline), h("roche-icon", { icon: this.icons, height: `${remCalc(24)}rem` })), h("div", { class: { content: true, 'content--visible': this.open }, ref: (element) => { this.contentElementParent = element; } }, h("div", null, h("roche-anchor", { scrollIntoElement: this.element.id }), h("slot", null)))));
    }
    handleClick() {
        this.toggleEvent.emit(this);
    }
    updateMaxHeight() {
        if (this.type === AccordionTabsType.TABS
            || !this.contentElementParent
            || !this.contentElement) {
            return;
        }
        // TODO replace the multiplication and be aware of the spacings as of right now
        // the math there is just a minor hack to go around the extra spacings that are not calculated
        // early.
        const height = this.open ? this.contentElement.getBoundingClientRect().height * 2 : 0;
        this.contentElementParent.setAttribute('style', `max-height:${height}px`);
    }
    manageMutationObserver() {
        var _a;
        if (!this.updateOnContentChange || ((_a = DomService.isMockedContext) === null || _a === void 0 ? void 0 : _a.call(DomService))) {
            return;
        }
        this.mutationObserver = new MutationObserver(this.updateMaxHeight);
        this.mutationObserver.observe(this.element, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    }
    get element() { return this; }
    static get watchers() { return {
        "open": ["handleItemOpenEvent"]
    }; }
    static get style() { return RocheAccordionTabsItemStyle0; }
}, [1, "roche-accordion-tabs-item", {
        "headline": [1],
        "icon": [1],
        "type": [1],
        "isOverflowYScrollable": [4, "is-overflow-y-scrollable"],
        "appearance": [513],
        "open": [4],
        "enableJumpmark": [4, "enable-jumpmark"],
        "updateOnContentChange": [4, "update-on-content-change"],
        "getTabIndex": [64],
        "ensureVisibility": [64]
    }, undefined, {
        "open": ["handleItemOpenEvent"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-accordion-tabs-item", "roche-anchor", "roche-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-accordion-tabs-item":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheAccordionTabsItem);
            }
            break;
        case "roche-anchor":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RocheAccordionTabsItem as R, defineCustomElement as d };

//# sourceMappingURL=roche-accordion-tabs-item2.js.map